import { useLogger } from "@kensho/lumberjack";
import clsx from "clsx";
import { use, useMemo } from "react";

import { useTranscriptContext } from "../../../providers/use-transcript-context";
import {
  APITranscriptSlice,
  LabelAnnotation,
  Stage,
} from "../../../types/types";
import { produceEditorAction } from "../../../utils/transcript-revision-utils";
import { TranscriptPermissionsContext } from "../transcript-permissions-provider";
import { useDispatchEditorAction } from "./dispatch-editor-action-provider";

interface Props {
  stage: Stage;
  slice: APITranscriptSlice;
  sliceIndex: number;
}
export function KeyQuestionToggle(props: Props) {
  const { stage, slice, sliceIndex } = props;
  const { dispatchEditorAction } = useDispatchEditorAction();
  const { transcript, version } = useTranscriptContext();
  const { transcriptPermissions } = use(TranscriptPermissionsContext);
  const log = useLogger();

  const keyQuestionLabelAnnotation: LabelAnnotation | null = useMemo(() => {
    if (!transcript.annotations) return null;

    const annotationRef = slice.annotations?.find(
      ({ id }) => transcript.annotations?.[id]?.source === "label",
    );
    if (annotationRef) {
      const labelAnnotation = transcript.annotations[annotationRef.id];
      if (labelAnnotation.label === "Key Question") {
        return labelAnnotation;
      }
    }
    return null;
  }, [slice.annotations, transcript.annotations]);

  function addKeyQuestion(): void {
    if (!transcriptPermissions.edit) return;

    const existingLabelAnnotation = Object.values(
      transcript.annotations || {},
    ).find(
      (annotation) =>
        annotation.source === "label" && annotation.label === "Key Question",
    );

    const editorAction = produceEditorAction({
      action: {
        type: "add-annotation",
        annotation: {
          source: "label",
          knowledgeBase: "custom",
          label: "Key Question",
          type: "manual",
          ...(existingLabelAnnotation
            ? { id: existingLabelAnnotation.id }
            : {}),
        },
        annotationId: existingLabelAnnotation?.id,
        ranges: [{ start: { sliceIndex }, end: { sliceIndex } }],
      },
      transcript,
      transcriptSelection: null,
      log,
    });
    if (!editorAction) return;

    dispatchEditorAction(editorAction, version);
  }

  function removeKeyQuestion(): void {
    if (!keyQuestionLabelAnnotation) return;
    if (!transcriptPermissions.edit) return;

    const editorAction = produceEditorAction({
      action: {
        type: "delete-annotation",
        annotationId: keyQuestionLabelAnnotation.id,
        ranges: [{ start: { sliceIndex }, end: { sliceIndex } }],
      },
      transcript,
      transcriptSelection: null,
      log,
    });
    if (!editorAction) return;

    dispatchEditorAction(editorAction, version);
  }

  if (stage !== "POST_TRANSCRIPTION") return null;

  return (
    <button
      type="button"
      onClick={() => {
        if (!transcriptPermissions.edit) return;
        if (keyQuestionLabelAnnotation) {
          removeKeyQuestion();
        } else {
          addKeyQuestion();
        }
      }}
      className={clsx(
        "inline-flex h-6 flex-none items-center rounded-2xl border px-2 py-1 text-xs transition-colors duration-200 select-none",
        keyQuestionLabelAnnotation
          ? `border-transparent bg-cyan-700 text-white`
          : `border-gray-400 text-gray-400`,
      )}
    >
      Key question
    </button>
  );
}
